import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../services/supabase';
import { useAuth } from './useAuth';
import type { SupplierMitigationTask } from '../types';

export function useSupplierMitigationTasks(supplierId?: string) {
  const { companyId } = useAuth();
  const queryClient = useQueryClient();

  const query = useQuery<SupplierMitigationTask[]>({
    queryKey: ['supplier_mitigation_tasks', supplierId ?? 'all', companyId],
    queryFn: async () => {
      let q = supabase
        .from('supplier_mitigation_tasks')
        .select('*')
        .order('priority', { ascending: false })
        .order('created_at', { ascending: true });
      if (supplierId) {
        q = q.eq('supplier_id', supplierId);
      }
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as SupplierMitigationTask[];
    },
    enabled: true,
    staleTime: 60000,
  });

  const insertMutation = useMutation({
    mutationFn: async (row: Partial<SupplierMitigationTask>) => {
      const { data, error } = await supabase
        .from('supplier_mitigation_tasks')
        .insert(row)
        .select()
        .single();
      if (error) throw error;
      return data as SupplierMitigationTask;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['supplier_mitigation_tasks'] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<SupplierMitigationTask> & { id: string }) => {
      const { data, error } = await supabase
        .from('supplier_mitigation_tasks')
        .update({ ...updates, embedding: null })
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data as SupplierMitigationTask;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['supplier_mitigation_tasks'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('supplier_mitigation_tasks').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['supplier_mitigation_tasks'] });
    },
  });

  return {
    tasks: query.data ?? [],
    loading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    insertTask: insertMutation.mutateAsync,
    updateTask: updateMutation.mutateAsync,
    deleteTask: deleteMutation.mutateAsync,
  };
}
